import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Text, Button, Title, SimpleGrid } from '@repletoryreach/sdk-ui';
import { NumberInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const KEY = ['admin', 'extra-use-settings'];

type Amount = 'max_limit_minor' | 'min_limit_minor' | 'min_step_minor';

/** Each amount's range in cents, as the service checks it; `null` for "up to the highest limit". */
const AMOUNTS: Array<{ key: Amount; min: number; max: number | null }> = [
  { key: 'max_limit_minor', min: 10_000, max: 10_000_000 },
  { key: 'min_limit_minor', min: 1_000, max: null },
  { key: 'min_step_minor', min: 1_000, max: null }
];

/** Typed in rand, stored in cents. */
const toMajor = (minor: number) => Math.round(minor) / 100;
const toMinor = (major: number | '') => (major === '' ? null : Math.round(major * 100));

/**
 * Extra use (owner, 30 October 2026): businesses on Growth and Scale may keep going after their credit runs out,
 * charged to their saved card at their plan's prices, up to a monthly limit they set. These four settings decide
 * whether it is offered at all and the bounds every business chooses within. The highest limit is the most one
 * business can owe the platform in a month.
 */
export function ExtraUseSettingsCard() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => admin.getExtraUseSettings(), retry: false });
  const saved = q.data?.data;
  const [offered, setOffered] = useState(true);
  const [draft, setDraft] = useState<Record<Amount, number | ''>>({ max_limit_minor: '', min_limit_minor: '', min_step_minor: '' });
  const [serverError, setServerError] = useState<{ field: string; message: string } | null>(null);
  // Seeded once, and again after a save: a refetch in the background must not wipe what is being typed.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!saved || seeded) return;
    setOffered(saved.offered);
    setDraft({ max_limit_minor: toMajor(saved.max_limit_minor), min_limit_minor: toMajor(saved.min_limit_minor), min_step_minor: toMajor(saved.min_step_minor) });
    setSeeded(true);
  }, [saved, seeded]);

  const save = useMutation({
    mutationFn: (req: admin.ExtraUseSettings) => admin.saveExtraUseSettings(req),
    onMutate: () => setServerError(null),
    onSuccess: (res) => {
      qc.setQueryData(KEY, res);
      setSeeded(false);
      notifySuccess(t('billing.extraUse.saved'), t('billing.extraUse.title'));
    },
    onError: (e) => {
      const field = isApiError(e) ? (e.body as { error?: { field?: string } } | null)?.error?.field : undefined;
      if (isApiError(e) && e.code === 'VALIDATION' && field) { setServerError({ field, message: e.message }); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.extraUse.title'));
    }
  });

  const maxMinor = toMinor(draft.max_limit_minor);
  const errorOf = (key: Amount): string | undefined => {
    if (serverError?.field === key) return serverError.message;
    const v = toMinor(draft[key]);
    const range = AMOUNTS.find((a) => a.key === key)!;
    const upper = range.max ?? maxMinor ?? range.min;
    if (v == null || v < range.min || v > upper) {
      return t('billing.extraUse.invalid', { min: formatMinor(range.min, 'ZAR'), max: formatMinor(upper, 'ZAR') });
    }
    return undefined;
  };
  const invalid = AMOUNTS.some(({ key }) => errorOf(key) !== undefined);
  const dirty = Boolean(saved && (offered !== saved.offered || AMOUNTS.some(({ key }) => toMinor(draft[key]) !== saved[key])));

  if (q.error) return null;

  return (
    <Card withBorder data-testid="extra-use-settings">
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={5}>{t('billing.extraUse.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.extraUse.body')}</Text>
        </Stack>
        <Switch
          checked={offered}
          onChange={setOffered}
          label={t('billing.extraUse.offered.label')}
          description={t('billing.extraUse.offered.hint')}
          disabled={!saved}
        />
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          {AMOUNTS.map(({ key, min }) => (
            <NumberInput
              key={key}
              size="sm"
              label={t(`billing.extraUse.${key}.label`)}
              description={t(`billing.extraUse.${key}.hint`, { min: formatMinor(min, 'ZAR') })}
              value={draft[key]}
              onChange={(v) => { setDraft((d) => ({ ...d, [key]: v })); setServerError(null); }}
              min={0}
              step={100}
              error={saved ? errorOf(key) : undefined}
              disabled={!saved}
            />
          ))}
        </SimpleGrid>
        <Group justify="flex-end">
          <Button size="xs" loading={save.isPending} disabled={!dirty || invalid}
            onClick={() => save.mutate({
              offered,
              max_limit_minor: toMinor(draft.max_limit_minor)!,
              min_limit_minor: toMinor(draft.min_limit_minor)!,
              min_step_minor: toMinor(draft.min_step_minor)!
            })}>
            {t('action.save')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
